import { useEffect, useReducer, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  ExperimentEditSchema,
  type ExperimentStatusSchema,
  GroupExperimentSchema,
} from '@/zod_schemas'; // Adjust your schema imports
import { ProjectFormElement } from '../StyledComponents';
import ProjectEditHeader from '../Components/ProjectEditHeader';
import Section from './Components/Section';
import {
  useEditProject,
  useEditProjectFormHandler,
  useExperimentFormHandler,
  useUpdateExperiment,
} from '../hooks';
import SamplesContainer2 from './Components/SamplesContainer2';
import { useGetExperimentsGroupSamples } from '@/api/project/project';
import { useExperimentStatus } from '@/api/experiment/experiment';

interface ExperimentGroupFormProps {
  experimentGroup?: ExperimentEditSchema;
  isPolling?: boolean;
}

interface ExperimentFormProps {
  experiment?: GroupExperimentSchema;
  experimentIndex: number;
  isPolling?: boolean;
}

// The state will be an array of ExperimentGroup
type ExperimentGroupState = ExperimentEditSchema[];

// Action types for the reducer
type ExperimentGroupAction =
  | { type: 'ADD_GROUP'; payload: ExperimentEditSchema }
  | { type: 'SET_GROUPS'; payload: ExperimentEditSchema[] };

const getStatusPresentation = (status: ExperimentStatusSchema) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        color: 'success' as const,
        icon: <CheckCircleOutlineIcon />,
      };
    case 'in_progress':
      return {
        label: 'In progress',
        color: 'info' as const,
        icon: <AutorenewIcon />,
      };
    case 'failed':
      return {
        label: 'Failed',
        color: 'error' as const,
        icon: <ErrorOutlineIcon />,
      };
    case 'waiting':
      return {
        label: 'Waiting',
        color: 'warning' as const,
        icon: <HourglassTopIcon />,
      };
    default:
      return {
        label: 'Pending',
        color: 'default' as const,
        icon: <ScheduleIcon />,
      };
  }
};

// Reducer to manage experiment group state
/* The useReducer hook is used to manage the state
   of the experiment groups in a structured and scalable way.
  ** ADD_GROUP: Adds a new experiment group to the current state.
  ** SET_GROUPS: Allows for completely replacing the current state with new experiment groups.
*/
const experimentGroupsReducer = (
  state: ExperimentGroupState,
  action: ExperimentGroupAction,
) => {
  switch (action.type) {
    case 'ADD_GROUP':
      return [...state, action.payload];
    case 'SET_GROUPS':
      return action.payload;
    default:
      return state;
  }
};

const ExperimentForm = ({
  experiment,
  experimentIndex,
  isPolling,
}: ExperimentFormProps) => {
  const {
    handleSubmit,
    control,
    setValue,
    experimentId = experiment?.id,
    formState: { errors, isSubmitting },
  } = useExperimentFormHandler(experiment!);

  const { fields: sampleFields } = useFieldArray({
    control,
    name: 'samples',
    keyName: 'ids',
  });

  // Ensure every field has a valid `id` or fallback to `_id` -> to prevent useFieldArray bug
  const normalizedFields = sampleFields.map((field) => ({
    ...field,
    id: field.id || field.ids, // Ensure `id` is defined
  }));

  const { data, refetch } = useExperimentStatus({ id: experimentId });
  const responseExpStatus = data?.status;

  const onSubmit = useUpdateExperiment(experimentId);

  const [experimentStatus, setExperimentStatus] = useState(responseExpStatus);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (
      isPolling &&
      responseExpStatus !== 'completed' &&
      responseExpStatus !== 'failed'
    ) {
      intervalId = setInterval(async () => {
        try {
          // Call API to fetch updated status
          const { data: updatedData } = await refetch(); // Use refetch from the hook
          const updatedStatus = updatedData?.status;
          setExperimentStatus(updatedStatus);
          // Update form field with new status
          setValue('status', updatedStatus);
          if (updatedStatus === 'completed' || updatedStatus === 'failed') {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        } catch (error) {
          console.error('Error updating status:', error);
        }
      }, 60000); // Poll every 1 hour
    }

    return () => {
      // Cleanup on component unmount or when polling stops
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    isPolling,
    responseExpStatus,
    experimentStatus,
    experimentId,
    setValue,
    experiment?.id,
    refetch,
  ]);

  if (!experiment) {
    return <div>No experiments details. Please contact admin</div>;
  }

  const statusPresentation = getStatusPresentation(
    experimentStatus ?? experiment.status ?? 'pending',
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Stack spacing={0.25}>
            <Typography variant="h3">
              Experiment {experimentIndex + 1}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pipeline:{' '}
              <Box
                component="span"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                {experiment.flowgate_pipeline_id ??
                  experiment.pipeline_config?.name ??
                  '—'}
              </Box>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              icon={statusPresentation.icon}
              label={statusPresentation.label}
              color={statusPresentation.color}
              size="small"
              variant="outlined"
            />
            <Tooltip title="Refresh experiment status">
              <IconButton
                aria-label="refresh experiment status"
                size="small"
                onClick={async () => {
                  // Call API to fetch updated status
                  const { data: updatedData } = await refetch(); // Use refetch from the hook
                  const updatedStatus = updatedData?.status;
                  setExperimentStatus(updatedStatus);
                  // Update form field with new status
                  setValue('status', updatedStatus);
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        {normalizedFields.length > 0 && (
          <Grid item xs={12}>
            <SamplesContainer2
              control={control}
              experimentIndex={experimentIndex}
              experimentStatus={
                experimentStatus ?? experiment.status ?? 'pending'
              }
              errors={errors}
              fields={normalizedFields}
            />
          </Grid>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              size="small"
              sx={{ flex: { xs: 1, sm: 'initial' } }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Run experiment'
              )}
            </Button>
            <Tooltip title="Remove experiment">
              <Button
                aria-label="remove experiment"
                // Existing placeholder handler retained until deletion behavior is defined.
                // eslint-disable-next-line no-console
                onClick={() => console.log('delete experiment')}
                variant="outlined"
                color="error"
                size="small"
                sx={{ flex: { xs: 1, sm: 'initial' } }}
              >
                Remove
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
};

const ExperimentGroupForm = ({
  experimentGroup,
  isPolling,
}: ExperimentGroupFormProps) => (
  <Stack spacing={2}>
    {experimentGroup ? (
      experimentGroup?.experiments.map((experiment, experimentIndex) => (
        <Stack spacing={2} key={experiment.id}>
          <ExperimentForm
            experiment={experiment}
            experimentIndex={experimentIndex}
            isPolling={isPolling}
          />
        </Stack>
      ))
    ) : (
      <div>No experiments details. Please contact admin</div>
    )}
  </Stack>
);

const ProjectEditForm = () => {
  const {
    handleSubmit,
    register,
    control,
    projectId,
    formState: { errors, isSubmitting },
  } = useEditProjectFormHandler();
  const onSubmit = useEditProject(projectId);
  const [isPolling, setIsPolling] = useState(false);

  const { fields: projectFields } = useFieldArray({
    control,
    name: 'experiment_groups',
    keyName: '_id',
  });
  const [state, dispatch] = useReducer(experimentGroupsReducer, []);
  const [experimentGroupsID, setExperimentGroupsID] = useState<string>('');
  const {
    data: experiment,
    isLoading,
    error,
  } = useGetExperimentsGroupSamples(experimentGroupsID);

  useEffect(() => {
    if (experiment && experiment.id) {
      dispatch({ type: 'ADD_GROUP', payload: experiment });
    }
  }, [experiment]);

  if (!projectFields) return <div>Loading...</div>;

  return (
    <Box sx={{ width: '100%', maxWidth: 1240, mx: 'auto', pb: 3 }}>
      <ProjectFormElement
        onSubmit={handleSubmit(onSubmit)}
        sx={{ maxWidth: 'none', pb: 3 }}
      >
        <ProjectEditHeader control={control} isSubmitting={isSubmitting} />
        <Section
          title="Project details"
          description="Update the project name and description used across the clinical workspace."
        >
          <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
            <TextField
              id="project-name"
              label="Project name"
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              {...register('name')}
              fullWidth
            />
            <TextField
              id="project-description"
              label="Description"
              error={Boolean(errors.description)}
              helperText={errors.description?.message}
              {...register('description')}
              fullWidth
            />
          </Stack>
        </Section>
      </ProjectFormElement>

      <Stack spacing={2} useFlexGap sx={{ width: '100%' }}>
        <Stack spacing={0.25}>
          <Typography variant="h2">Experiment groups</Typography>
          <Typography variant="body2" color="text.secondary">
            Review the experiments, pipeline status, and samples associated with
            this project.
          </Typography>
        </Stack>
        {projectFields.map((experimentGroup) => (
          <Accordion
            key={experimentGroup.id}
            onChange={(_, expanded) => {
              setExperimentGroupsID(experimentGroup.id);
              setIsPolling(expanded);
            }}
            disableGutters
            elevation={0}
            sx={(theme) => ({
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                m: 0,
                borderColor: 'divider',
                boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
              },
            })}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => ({
                minHeight: 68,
                px: { xs: 2, md: 2.5 },
                transition: theme.transitions.create('background-color', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-expanded': {
                  minHeight: 68,
                  bgcolor: 'action.selected',
                },
                '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
              })}
            >
              <Stack spacing={0.25}>
                <Typography variant="subtitle1">
                  {experimentGroup.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Experiment group
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: { xs: 2, md: 2.5 },
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {isLoading && (
                <Typography>Loading experiment details…</Typography>
              )}
              {error && (
                <Typography color="error">
                  Error loading experiment group
                </Typography>
              )}
              {!isLoading &&
                !error &&
                (state.find((group) => group.id === experimentGroup.id) ? (
                  <ExperimentGroupForm
                    experimentGroup={
                      state.find((group) => group.id === experimentGroup.id)!
                    }
                    isPolling={isPolling}
                  />
                ) : (
                  <Typography color="text.secondary">
                    No experiment group found
                  </Typography>
                ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
};

export default ProjectEditForm;
